import React, { useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ThemeProvider, StyledEngineProvider, createTheme, adaptV4Theme } from "@mui/material/styles";
import { ThemeProvider as LegacyStylesThemeProvider } from "@mui/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { checkSession } from "modules/login/redux/session.action";
import LoginPage from "modules/login/pages/login.page";
import AdminLayout from "components/layout/AdminLayout";
import NotFound from "modules/not-found/not-found.page";
import store from "app/config/store";
import { installAxiosInterceptors } from "app/config/axios";

installAxiosInterceptors(store);

const theme = createTheme(adaptV4Theme({
  palette: {
    primary: { main: "#3A4D54" },
    secondary: { main: "#347AE2" },
    background: { default: "#EBEDEE", paper: "#FFFFFF" },
    text: { primary: "#0F151E", secondary: "#7C8DB5" },
    success: { main: "#32D583", light: "#ECFDF3" },
    error: { main: "#FD646F" },
    divider: "#D0D5DD",
  },
  typography: {
    fontFamily: "'Roboto', sans-serif",
  },
  shape: {
    borderRadius: 12,
  },
  overrides: {
    MuiPaper: {
      rounded: { borderRadius: 12 },
    },
    MuiTableHead: {
      root: {
        backgroundColor: "#F9FAFB",
        "& .MuiTableCell-head": {
          color: "#3A4D54",
          fontWeight: 500,
          fontSize: 14,
          borderBottom: "1px solid #F2F4F7",
        },
      },
    },
    MuiTableCell: {
      root: {
        fontSize: 14,
        color: "#0F151E",
        borderBottom: "1px solid #F2F4F7",
      },
    },
    MuiTableContainer: {
      root: {
        border: "1px solid #D0D5DD",
        borderRadius: 12,
        boxShadow: "none",
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 4,
        overflow: "hidden",
        boxShadow: "0px 4px 4px rgba(58,77,84,0.25)",
      },
    },
    MuiDialogTitle: {
      root: {
        backgroundColor: "#FFFFFF",
        boxShadow: "0px 1px 0px rgba(58,77,84,0.15)",
        padding: "12px 20px",
        "& .MuiTypography-root": {
          fontWeight: 700,
          fontSize: 13,
          color: "#3A4D54",
        },
      },
    },
    MuiDialogContent: {
      root: {
        backgroundColor: "#EBEDEE",
        padding: 10,
      },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: "#FFFFFF",
        boxShadow: "0px -1px 0px rgba(58,77,84,0.15)",
        padding: "14px 20px",
      },
    },
    MuiButton: {
      root: {
        textTransform: "none",
        fontWeight: 700,
        borderRadius: 4,
      },
    },
    MuiChip: {
      root: { fontWeight: 500, borderRadius: 6 },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#D0D5DD",
        },
      },
    },
  },
}));

function PrivateRoute({ children, ...rest }) {
  const token = useSelector((state) => state.session.token);
  return (
    <Route
      {...rest}
      render={({ location }) =>
        token ? children : <Redirect to={{ pathname: "/login", state: { from: location } }} />
      }
    />
  );
}

export default function App() {
  const dispatch = useDispatch();
  const isSessionChecked = useSelector((state) => state.session.isSessionChecked);

  useEffect(() => {
    dispatch(checkSession());
  }, [dispatch]);

  if (!isSessionChecked) {
    return null;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LegacyStylesThemeProvider theme={theme}>
          <CssBaseline />
          <Switch>
            <Route exact path="/login" component={LoginPage} />
            <PrivateRoute path="/admin">
              <AdminLayout />
            </PrivateRoute>
            <Route path="/404" component={NotFound} />
            <Redirect exact from="/" to="/admin" />
            <Redirect to="/404" />
          </Switch>
        </LegacyStylesThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
